import type { CSSProperties } from 'react';

import { groupNamePresenter, taskNamePresenter } from '@/Client/Presenters';
import type { OverallTaskStatus } from '@kaiizen/kai-pro-api';
import {
  getTotalCostIfDefined,
  isTaskScoped,
  isTaskShownOnScopeReport,
  type ScanGroupOptimized,
  type ScanLineItemOptimized,
  type ScanPartOptimized,
  type ScanPhoto,
  type ScanTaskOptimized,
  type ScanVideo,
} from '@kaiizen/kai-scan-template-system';

/**
 * Shared, pure scan-part presenters consumed by BOTH the ScopeNavigator sidebar
 * and the closeout review pipeline (ComparisonPane / Flow / closeoutModelBuilders).
 *
 * These are typed directly on the optimized scan types so callers pass parts
 * through without a bespoke view model. Surface-specific labels/formatters live
 * with their component; closeout-pipeline-only transforms live in
 * `../Partials/CloseoutReviewFlow/closeoutModelBuilders`.
 */

// ************************************************************************
// Presentation context (threaded from the consuming component)
// ************************************************************************

/**
 * The presentation context threaded from the consuming component (rather than
 * baked into a view model): approval state + the contractor resolver.
 */
export type ScopeNavigatorContext = {
  approvedTaskIds?: ReadonlySet<string>;
  /**
   * Each task's whole-life status, resolved once by the framework — take it from
   * `ScanEditingContext.overallTaskStatusByTaskId` rather than recomputing, so
   * every surface reports the same task the same way.
   */
  overallTaskStatusByTaskId?: Record<string, OverallTaskStatus>;
  /**
   * Resolves a task's contractor, honouring the scan's defaults-by-trade — so
   * a task with no explicit assignment still reports the contractor who will
   * actually do it. `id` is the org contractor id: the stable key the Contractor
   * tab buckets and routes by.
   */
  getContractorForTask?: (
    task: ScanTaskOptimized,
  ) => { id?: string; name?: string; trades?: readonly string[] } | undefined;
};

// ************************************************************************
// Color identity (7-color reusable task-group palette)
// ************************************************************************

export type ColorToken =
  | 'group-1'
  | 'group-2'
  | 'group-3'
  | 'group-4'
  | 'group-5'
  | 'group-6'
  | 'group-7';

/** A palette swatch: a stable token + its hex value. */
export type ColorSwatch = { token: ColorToken; value: string };

export const COLOR_PALETTE: readonly ColorSwatch[] = [
  { token: 'group-1', value: '#a96947' },
  { token: 'group-2', value: '#6aa77d' },
  { token: 'group-3', value: '#59a7d4' },
  { token: 'group-4', value: '#d0a64f' },
  { token: 'group-5', value: '#8a7cb8' },
  { token: 'group-6', value: '#d96f5f' },
  { token: 'group-7', value: '#4e7fab' },
];

/** The swatch for a group's array position, wrapping after seven groups. */
export const getColorSwatch = (groupIndex: number): ColorSwatch => COLOR_PALETTE[groupIndex % COLOR_PALETTE.length];

/** Exposes a swatch's hex as the `--closeout-group-color` CSS variable. */
export const getColorStyle = (
  swatch: Pick<ColorSwatch, 'value'>,
): CSSProperties & Record<'--closeout-group-color', string> => ({
  '--closeout-group-color': swatch.value,
});

// ************************************************************************
// Names
// ************************************************************************

export const getGroupTitle = (group: ScanGroupOptimized, fallback = 'Untitled group'): string =>
  group.template?.name?.trim() ? groupNamePresenter(group) : fallback;

export const getTaskTitle = (task: ScanTaskOptimized, fallback = 'Untitled task'): string => task.template?.name?.trim() ? taskNamePresenter(task) : fallback;

/** A pluralized task-count label, e.g. `1 task` / `3 tasks`. */
export const getTaskCountLabel = (taskCount: number): string => `${taskCount} ${taskCount === 1 ? 'task' : 'tasks'}`;

/**
 * Display name for a line item. Mirrors `InlineLineItemEditor`'s priority: the
 * selected product's title wins (from the `skuDetails` snapshot captured at
 * selection), falling back to the template (placeholder) name, then a generic
 * label. A `customLabel`, when set, is appended in parentheses.
 */
export const getLineItemName = (lineItem: ScanLineItemOptimized): string => {
  const baseName = lineItem.skuDetails?.title?.trim() || lineItem.template?.name?.trim() || 'Line item';
  return lineItem.customLabel ? `${baseName} (${lineItem.customLabel})` : baseName;
};

// ************************************************************************
// Cost
// ************************************************************************

/**
 * Safe total cost for a scan part. `getTotalCostIfDefined` reaches into
 * `optimized.reducedValues`; presentation inputs and unit-test fixtures may carry
 * an `optimized` object without `reducedValues`, so we treat a missing
 * `reducedValues` as "no cost" (`undefined`) instead of crashing.
 */
export const getPartCostValue = (part: ScanPartOptimized | undefined): number | undefined => {
  if (!part?.optimized?.reducedValues) return undefined;
  return getTotalCostIfDefined(part);
};

// ************************************************************************
// Photos
// ************************************************************************

export const getPartPhotoEntries = (part: ScanPartOptimized | undefined): ScanPhoto[] => {
  const photoRecords = [...(part?.optimized?.allPhotos ?? []), ...(part?.photos ?? [])];
  const photoRecordById = new Map(
    photoRecords.flatMap(photo => photo.photoId ? [[photo.photoId, photo] as const] : []),
  );
  const orderedIds = part?.optimized?.allPhotoIds?.filter(Boolean) ?? [];
  const candidates: ScanPhoto[] = orderedIds.length
    ? orderedIds.map(photoId => ({ ...photoRecordById.get(photoId), photoId }))
    : photoRecords.filter((photo): photo is ScanPhoto => Boolean(photo.photoId));
  const seen = new Set<string>();
  return candidates.filter(photo => {
    if (seen.has(photo.photoId)) return false;
    seen.add(photo.photoId);
    return true;
  });
};

export const getPartPhotoIds = (part: ScanPartOptimized | undefined): string[] => getPartPhotoEntries(part).map(photo => photo.photoId);

/** Local `videos[]` on this part only — no descendant rollup from `optimized.allVideos`. */
export const getPartLocalVideoEntries = (part: ScanPartOptimized | undefined): ScanVideo[] => {
  const localVideos = (part as { videos?: ScanVideo[] } | undefined)?.videos ?? [];
  const seen = new Set<string>();
  return localVideos.filter((video): video is ScanVideo => {
    if (!video.videoId || seen.has(video.videoId)) return false;
    seen.add(video.videoId);
    return true;
  });
};

export const getPartVideoEntries = (part: ScanPartOptimized | undefined): ScanVideo[] => {
  const localVideos = (part as { videos?: ScanVideo[] } | undefined)?.videos;
  const videoRecords = [...(part?.optimized?.allVideos ?? []), ...(localVideos ?? [])];
  const videoRecordById = new Map(
    videoRecords.flatMap(video => video.videoId ? [[video.videoId, video] as const] : []),
  );
  const orderedIds = part?.optimized?.allVideoIds?.filter(Boolean) ?? [];
  const candidates: ScanVideo[] = orderedIds.length
    ? orderedIds.map(videoId => ({ ...videoRecordById.get(videoId), videoId }))
    : videoRecords.filter((video): video is ScanVideo => Boolean(video.videoId));
  const seen = new Set<string>();
  return candidates.filter(video => {
    if (seen.has(video.videoId)) return false;
    seen.add(video.videoId);
    return true;
  });
};

export const getPartVideoIds = (part: ScanPartOptimized | undefined): string[] => getPartVideoEntries(part).map(video => video.videoId);

// ************************************************************************
// Contractor
// ************************************************************************

export const getContractorLine = (task: ScanTaskOptimized, context?: ScopeNavigatorContext): string | undefined => context?.getContractorForTask?.(task)?.name;

export const getContractorTrade = (task: ScanTaskOptimized, context?: ScopeNavigatorContext): string | undefined =>
  task.trade ?? context?.getContractorForTask?.(task)?.trades?.join(', ');

/**
 * The bucket for tasks no contractor resolves to. Deliberately not shaped like
 * an org contractor id, so it can never collide with one.
 */
export const UNASSIGNED_CONTRACTOR_ID = '__unassigned';

/**
 * Whether a task belongs to the given contractor bucket — the same resolution
 * the Contractor tab buckets tasks by (`getContractorForTask`, which already
 * honours the scan's default-by-trade), so a task the tab lists under a
 * contractor is the same task this reports as matching.
 */
export const taskMatchesContractor = (
  task: ScanTaskOptimized,
  contractorId: string,
  context?: ScopeNavigatorContext,
): boolean => {
  const taskContractorId = context?.getContractorForTask?.(task)?.id;
  return contractorId === UNASSIGNED_CONTRACTOR_ID ? !taskContractorId : taskContractorId === contractorId;
};

// ************************************************************************
// Scope selection
// ************************************************************************

/**
 * Safe `scoped` state for a scan task. `isTaskScoped` reaches into
 * `optimized.reducedValues`; when cost data is absent (partial inputs / fixtures)
 * we return `undefined` (the pre-`scoped` behavior) instead of crashing.
 */
export const isTaskScopedSafe = (task: ScanTaskOptimized): boolean | undefined => {
  if (task.optimized && !task.optimized.reducedValues) return undefined;
  return isTaskScoped(task);
};

/**
 * Which tasks the nav shows: the ones on the scope report, plus any the user added by hand.
 *
 * "On the scope report" rather than "scoped", the broader of the two: a task carrying a measurement or
 * a photo but no cost is still part of the job, and the nav is where anyone goes looking for it. Asking
 * the same question the report asks keeps the two from disagreeing about what the scan contains.
 *
 * A hand-added task shows whether or not it has anything in it yet — adding it was the statement.
 */
export const isScopeNavigatorTask = (task: ScanTaskOptimized): boolean => isTaskShownOnScopeReport(task) || task.userAdded === true;
